import { Component, OnInit } from '@angular/core';

// Services
import { UsersService } from '@modules/users/services/users.service';

// Components
import { AddUserComponent } from '../add-user/add-user.component';

// Types
import { MatDialog } from '@angular/material/dialog';
import { TeamUsers, User } from '@modules/users/types/user';
import { catchError, filter, of, Subject, switchMap, takeUntil } from 'rxjs';
import { AddTeamComponent } from '../add-team/add-team.component';
import { Account } from '@modules/users/types/account';
import { AuthService } from '@modules/auth/services/auth.service';
import { NotificationService } from '@modules/elements/services/notification.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { AlertService } from '@modules/elements/services/alert.service';


@Component({
    selector: 'app-users-management',
    templateUrl: './users-management.component.html',
    styleUrl: './users-management.component.less',
    standalone: false
})
export class UsersManagementComponent implements OnInit {

  // Public
  public users: User[] = [];
  public teamUsers: TeamUsers[] = [];
  public account?: Account;
  public canManageTeams = false;

  // Private
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    private usersService: UsersService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private alertService: AlertService,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    // Get Account
    this.usersService.getAccount()
      .pipe(takeUntil(this.alive))
      .subscribe(account => this.account = account);
    // Get Current team
    this.usersService.getUsers()
      .pipe(takeUntil(this.alive))
      .subscribe(users => this.users = users);
    // Get Team Users
    this.usersService.getTeamUsers()
      .pipe(takeUntil(this.alive))
      .subscribe(users => this.teamUsers = users);
    // Create / delete team require the 'admin:all' permission on the server
    this.usersService.hasAdminAccess()
      .pipe(takeUntil(this.alive))
      .subscribe(canManageTeams => this.canManageTeams = canManageTeams);
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }
  
  /**
   * Methods
   */

  createTeam(): void {
    this.dialog.open(AddTeamComponent, {data: {}, disableClose: true});
  }

  editTeam(team: TeamUsers): void {
    this.dialog.open(AddTeamComponent, {data: {teamId: team.team?.id}, disableClose: true});
  }

  deleteTeam(team: TeamUsers): void {
    if (team.team?.id) {
      this.alertService.show(
        'Delete Team',
        `Would you like to delete '${team.team?.name}' team?`,
        ['Yes', 'No'],
        0
      )
      .pipe(
        filter(response => response === 0),
        switchMap(() => this.usersService.deleteTeam(team.team?.id!)),
        takeUntil(this.alive),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Error delete team', message);
          }
          return of();
        })
      )
      .subscribe(response => {
        if (response) {
          this.notificationService.success('Team deleted successfully');
          this.teamUsers = this.teamUsers.filter(t => t.team?.id !== team.team?.id);
        }
      });
    }
  }

  inviteUser(): void {
    this.dialog.open(AddUserComponent, {data: {}, disableClose: true});
  }

  deleteUser(user: User): void {
    if (user.id) {
      this.usersService.deleteUser(user.id);
    }
  }

  regenerateToken(user: User): void {
    if (user.id) {
      this.authService.regenerateToken(user.id)
        .pipe(
          catchError(error => {
            if (error) {
              const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
              this.notificationService.error('Error invite link', message);
            }
            return of();
          }),
          takeUntil(this.alive)
        )
        .subscribe(respose => {
          if (respose?.link) {
            this.clipboard.copy(respose.link);
            this.notificationService.success('Copied new invite link', respose.link, false);
          }
        });
    }
  }

  resetPassword(user: User): void {
    if (user.id) {
      this.authService.resetPassword(user.id)
        .pipe(
          catchError(error => {
            if (error) {
              const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
              this.notificationService.error('Error reset password link', message);
            }
            return of();
          }),
          takeUntil(this.alive)
        )
        .subscribe(respose => {
          if (respose?.link) {
            this.clipboard.copy(respose.link);
            this.notificationService.success('Copied reset password link', respose.link, false);
          }
        });
    }
  }

  changeRole(user: User, role: string): void {
    user.role = role;
    this.usersService.updateUser(user);
  }

}
